import React from 'react'
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Upload,
  Button,
  Space,
  message,
} from 'antd'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import {
  LAYOUTSECTIONTYPECHARACTER,
  THUMBNAILSIZESCHARACTER,
} from 'src/constants'
import s from './index.module.scss'
const { TextArea } = Input

interface SectionFormModalProps {
  visible: boolean
  mode: 'add' | 'edit'
  form: any
  uploadedImage: File | null
  previewSrc: string
  onCancel: () => void
  onUpload: (info: any) => void
  onClearImage: () => void
  onSubmit: (values: any) => Promise<void>
  existingSections?: any[]
  isEditingExisting?: boolean
}

const SectionFormModal: React.FC<SectionFormModalProps> = ({
  visible,
  mode,
  form,
  uploadedImage,
  previewSrc,
  onCancel,
  onUpload,
  onClearImage,
  onSubmit,
}) => {
  const isEdit = mode === 'edit'

  const handleFinish = async (values) => {
    const size = values.thumbnailSize

    if (
      size !== 'LARGE' &&
      !uploadedImage &&
      !form.getFieldValue('iconS3Path')
    ) {
      message.error('Please upload an icon image before saving.')
      return
    }

    await onSubmit(values)
  }

  return (
    <Modal
      title={isEdit ? 'Edit Section' : 'Add New Section'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      className={s.editSectionModal}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark="optional"
      >
        <div className={s.formColumns}>
          <div className={s.formLeft}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Please enter section name' }]}
            >
              <Input placeholder="Enter section name" />
            </Form.Item>
            <Form.Item
              label="Title"
              name="title"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const size = getFieldValue('thumbnailSize')
                    if (size !== 'LARGE' && !value) {
                      return Promise.reject('Please enter section title')
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input placeholder="Enter section title" />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const size = getFieldValue('thumbnailSize')
                    if (size !== 'LARGE' && !value) {
                      return Promise.reject('Please enter description')
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <TextArea placeholder="Enter section description" rows={3} />
            </Form.Item>

            <Form.Item
              label="Section Type"
              name="type"
              rules={[
                { required: true, message: 'Please select section type' },
              ]}
            >
              <Select
                placeholder="Select section type"
                options={LAYOUTSECTIONTYPECHARACTER}
              />
            </Form.Item>
          </div>

          <div className={s.formRight}>
            <Form.Item
              label="Thumbnail Size"
              name="thumbnailSize"
              rules={[
                { required: true, message: 'Please select thumbnail size' },
              ]}
            >
              <Select
                placeholder="Select thumbnail size"
                options={THUMBNAILSIZESCHARACTER}
              />
            </Form.Item>

            <Form.Item
              label="Status"
              name="enable"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
            </Form.Item>

            <Form.Item
              label="View All"
              name="viewAll"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
            </Form.Item>

            {form.getFieldValue('type') === 'DYNAMIC' && (
              <Form.Item
                label="Auto Shuffle"
                name="autoShuffle"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                />
              </Form.Item>
            )}

            <Form.Item
              label="Icon Image"
              shouldUpdate={(prev, curr) =>
                prev.thumbnailSize !== curr.thumbnailSize
              }
            >
              {({ getFieldValue }) => {
                const size = getFieldValue('thumbnailSize')
                const isRequired = size !== 'LARGE'
                const hasImage = uploadedImage || getFieldValue('iconS3Path')

                return (
                  <Form.Item
                    validateStatus={isRequired && !hasImage ? 'error' : ''}
                    help={
                      isRequired && !hasImage ? 'Icon image is required' : ''
                    }
                  >
                    <Space className={s.imageActions}>
                      <Upload
                        onChange={onUpload}
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={() => false}
                      >
                        <Button icon={<UploadOutlined />} size="small">
                          Upload
                        </Button>
                      </Upload>
                      <Button
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                        onClick={onClearImage}
                      >
                        Clear
                      </Button>
                    </Space>
                  </Form.Item>
                )
              }}
            </Form.Item>

            <Form.Item name="iconS3Path" hidden>
              <Input />
            </Form.Item>

            <Form.Item name="sectionId" hidden />

            {previewSrc && (
              <div className={s.imagePreview}>
                <img
                  src={previewSrc}
                  alt="Icon preview"
                  className={s.previewImage}
                />
              </div>
            )}
          </div>
        </div>

        <div className={s.modalActions}>
          <Button type="primary" htmlType="submit" className={s.saveButton}>
            {isEdit ? 'Save Changes' : 'Save Section'}
          </Button>
          <Button onClick={onCancel} className={s.cancelButton}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

export default SectionFormModal
